import React, {useEffect, useRef} from 'react';

import {useSearchParams} from 'react-router-dom';

import springboard from 'springboard';

import './login_flow_styles.css';
import {StateSupervisor} from 'springboard/services/states/shared_state_service';
import {useModule} from '@/store/kysely_module';
import {DBObjects} from '@/store/store_types';
import initUserModule from './user_module';
import {notifications} from '@mantine/notifications';
import {Button, Group, Image, Paper, Text} from '@mantine/core';

// import {seedSampleData} from '@/store/seed/seed_sample_data';
import {seedSampleData} from '@/store/seed/mock_seed_sample_data';
import {generateId} from '@/store/utils/generate_id';
import {navigateToPathForReason} from '../navigation/navigation_module';
import {getIsTauriWebview, isReactNativeWeb} from '@/utils/user_agent';

initUserModule(springboard);

export type InvitePayload = {
    hash: string;
    messages: string[];
}

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        LoginFlow: LoginFlowModuleReturnValue;
    }
}

type LoginFormProps = {
    // nextStep: () => void;
}

export type LoginProvider = {
    form: React.ElementType<LoginFormProps>;
    useLogout: () => () => void;
};

type UserMetadata = {
    nickname: string;
    // "given_name": "TestFirst",
    // "family_name": "TestLast",
    // "nickname": "mynickname",
    // "name": "TestFirst TestLast",
    // "picture": "https://mypicture.com",
    // "updated_at": "2025-02-11T17:38:29.876Z",
    // "email": "myemail@gmail.com",
    // "email_verified": true,
    // "sub": "google-oauth2|some_hash"
}

type LoginFlowModuleReturnValue = {
    setLoginProvider: (provider: LoginProvider) => void;
    getLoginProvider: () => LoginProvider | undefined;
    resetStoredInvite: () => void;
};

springboard.registerModule('LoginFlow', {}, async (moduleAPI): Promise<LoginFlowModuleReturnValue> => {
    const storedInviteState = await moduleAPI.statesAPI.createUserAgentState<InvitePayload | null>('storedInviteState_v2', null);

    let loginProvider: LoginProvider | undefined;

    const workspacesModule = moduleAPI.deps.module.moduleRegistry.getModule('Workspaces');

    moduleAPI.registerRoute('/', {hideApplicationShell: true}, ({navigate}) => {
        useEffect(() => {
            navigateToPathForReason(navigate, 'auto_redirect_to_main_from_root', '/main');
        }, []);

        return '';
    });

    moduleAPI.registerRoute('/main', {hideApplicationShell: true}, ({navigate}) => {
        const LoginForm = loginProvider?.form;

        const userModule = useModule('User');
        const currentUser = userModule.currentUser.useState();
        const continueAsOffline = userModule.continueAsOffline;
        const isOfflineMode = userModule.isOfflineMode.useState();
        const kyselyModule = useModule('Kysely');
        const selectors = kyselyModule.selectors;
        const actions = kyselyModule.actions;
        const workspacesModule = useModule('Workspaces');

        const [searchParams, setSearchParams] = useSearchParams();

        const handleRedirect = () => {
            if (currentUser) {
                redirected.current = true;

                setTimeout(async () => {
                    if (!currentUser.name && currentUser.emailShort) {
                        // Auto-create user profile with email-based username
                        await userModule.updateProfile({
                            username: currentUser.emailShort,
                        });

                        // Auto-create first workspace
                        const workspace = await actions.createWorkspace({
                            name: `${currentUser.emailShort}'s Workspace`,
                            type: isOfflineMode ? 'LOCAL' : 'SHARED',
                        }, {workspace_id: null});

                        workspacesModule.setCurrentWorkspaceId(workspace.id);

                        const knownWorkspaces = workspacesModule.knownWorkspacesState.getState();
                        workspacesModule.setKnownWorkspaces([
                            ...knownWorkspaces,
                            {workspace, type: workspace.type === 'LOCAL' ? 'local' : 'remote'},
                        ]);

                        if (process.env.PUBLIC_SEED_SAMPLE_DATA === 'true') {
                            const seedSelectors = moduleAPI.getModule('Kysely').selectors;
                            const seedActions = moduleAPI.getModule('Kysely').actions;
                            await seedSampleData(seedSelectors, seedActions, isOfflineMode ? 'local' : 'remote');
                        }

                        // Check for stored invite after user creation
                        const inviteInfo = storedInviteState.getState();
                        if (inviteInfo) {
                            storedInviteState.setState(null);
                            moduleAPI.getModule('Navigation').setPathnameForReason('redirect_to_stored_invite_link_on_signup_success', `/invite/${inviteInfo.hash}`);
                            return;
                        }
                    }

                    navigateToPathForReason(navigate, 'navigate_to_dashboard_from_main_in_login_flow', '/dashboard');
                });

            } else {
                workspacesModule.setCurrentWorkspaceId('');

                const invitePayload = searchParams.get('invite');
                if (invitePayload) {
                    setSearchParams({});
                    storedInviteState.setState(JSON.parse(invitePayload));
                }
            }
        };

        const mounted = useRef(false);
        const redirected = useRef(false);
        if (!mounted.current) {
            mounted.current = true;
            handleRedirect();
        }

        useEffect(() => {
            if (!redirected.current) {
                handleRedirect();
            }
        }, [currentUser, isOfflineMode]);

        const inviteInfo = storedInviteState.useState();

        if (redirected.current) {
            return null;
        }

        return (
            <div
                className='login-page'
            >
                <Image
                    src='https://songdrive.app/assets/images/logo.png'
                    w='150px'
                    mb='xl'
                />

                {inviteInfo && (
                    <div style={{textAlign: 'center', marginBottom: '30px'}}>
                        {inviteInfo.messages.map(message => (
                            <h3 key={message}>
                                {message}
                            </h3>
                        ))}
                    </div>
                )}

                <Paper
                    radius="md"
                    p={{base: 'md', sm: 'lg'}}
                    withBorder
                    w={{base: 'calc(100% - 32px)', xs: '100%'}}
                    maw={{base: '100%', xs: 300}}
                    mx="auto"
                >
                    <Text size="lg" fw={500} ta="center">
                        Welcome to SongDrive
                    </Text>

                    {LoginForm && <LoginForm
                    />}

                    {!inviteInfo && <DefaultLoginForm
                        continueAsOffline={async () => {
                            continueAsOffline(navigate);
                        }}
                    />}
                </Paper>

                {/* <button
                    onClick={() => storedInviteState.setState(null)}
                >
                    Reset
                </button> */}
            </div>
        );
    });

    // Removed /create-workspace and /create-user routes - user and workspace are now auto-created

    return {
        setLoginProvider: provider => {
            loginProvider = provider;
        },
        getLoginProvider: () => loginProvider,
        resetStoredInvite: () => {
            storedInviteState.setState(null);
        },
    };
});

const DefaultLoginForm = (props: {continueAsOffline: () => void}) => {
    if (!process.env.PUBLIC_SHOW_CONTINUE_OFFLINE_BUTTON) {
        return null;
    }

    return (
        <>
            <Group grow mb="md" mt="md">
                <Text size='xs' fw={500} ta="center">
                    Online beta coming soon!
                </Text>
            </Group>

            <Group grow mb="md" mt="md">
                <Button
                    variant='default'
                    id='continue-offline-button'
                    radius="xl"
                    onClick={() => props.continueAsOffline()}
                >
                    Try Offline Demo
                </Button>
            </Group>
        </>
    );
};
